import { operators as o, common as c } from '@pendo/aggregations';
import { isDraftSegment } from '@/data-explorer/query-builder/query-selectors/segment-selector/helpers/segment-selector.helper.js';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { DEFAULT_SEGMENTS } from '@/utils/segments';
import { eligibleVisitorsPreviewLimit } from '@/segments/constants';

const selectAndGroup = () => [o.select({ visitorId: 'visitorId' }), o.group(['visitorId'])];

// copied this typing from the o.segment arg type
type SegmentParam = string | c.NestedSegment | c.SegmentPipeline | c.PipelineData[] | null | undefined;

function isNestedSegment (segment: SegmentParam | c.SimpleSegment): segment is c.NestedSegment {
    return !!segment && typeof segment === 'object' && 'compound' in segment && !!segment.compound;
}

function isDefaultSegment (id: string): boolean {
    const defaultIds = Object.keys(DEFAULT_SEGMENTS);

    return defaultIds.includes(id);
}

const getVisitorSource = (
    idPipelineOrSegment: SegmentParam,
    bulkExpandAccounts?: boolean,
    timeRange?: string,
    blacklist?: string,
    includeAnonymous?: string,
    filter?: string,
) => {
    // An empty string may be used to remove the "since" filter,
    // but it will default to 12 months if undefined.
    let since;
    if (timeRange === undefined) {
        since = 'dateAdd(now(), -12, "months")';
    } else {
        since = timeRange || undefined;
    }

    const pipeline = [
        o.sources.visitors({
            ...(blacklist && { blacklist }),
            since,
        }),
        includeAnonymous && o.identified(includeAnonymous),
        filter && o.filter(filter),
        o.evaluate('accountId', 'metadata.auto.accountids'),
        o.unwind('accountId', { keepEmpty: true }),
        o.segment(idPipelineOrSegment),
        ...(bulkExpandAccounts ? [o.bulkExpand('account'), o.bulkExpand('parentAccount')] : []),
        ...selectAndGroup(),
    ];

    return o.pipeline(...pipeline);
};

export const eligibleVisitorPipeline = ({
    segment,
    livePreview,
    noLimit,
    timeRange,
    index,
}: { livePreview: boolean; noLimit: boolean; timeRange?: string; index?: number } & c.PipelineSegment) => {
    const id = 'id' in segment ? segment.id : '';
    const useLocalPipeline = isDraftSegment(id) || livePreview;

    let visitorSource;

    if (isNestedSegment(segment)) {
        visitorSource = getVisitorSource(useLocalPipeline ? segment : id, true, timeRange);
    } else if (isDefaultSegment(id)) {
        let includeAnonymous;
        let blacklist;
        let filter;

        switch (id) {
            case 'noOne':
                filter = 'false';
                break;
            case 'everyoneExcludingAnonymous':
                includeAnonymous = 'visitorId';
                break;
            case 'anonymousVisitorsOnly':
                includeAnonymous = '!visitorId';
                break;
            case 'blacklistOnly':
                blacklist = 'only';
        }

        visitorSource = getVisitorSource(null, false, timeRange, blacklist, includeAnonymous, filter);
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const segmentParam: any = useLocalPipeline && ('compound' in segment || 'pipeline' in segment) ? segment : id;
        visitorSource = getVisitorSource(segmentParam, false, timeRange);
    }

    return o.pipeline(
        ...visitorSource,
        o.fork(
            o.pipeline(
                o.reduce({
                    [`eligibleVisitorCount${index || ''}`]: o.count(null),
                }),
            ),
            o.pipeline(
                !noLimit && o.limit(eligibleVisitorsPreviewLimit),
                o.sort('visitorId'),
                o.reduce({
                    visitorList: o.list('visitorId'),
                }),
            ),
        ),
        o.join(),
    );
};

export function visitorList ({
    name = 'segment-eligible-visitor-list',
    segment,
    livePreview,
    noLimit = false,
    timeRange,
}: {
    name?: string;
    segment: c.SimpleSegment | c.SegmentPipeline | c.NestedSegment;
    livePreview?: boolean;
    noLimit?: boolean;
    timeRange?: string;
}) {
    return o.aggregation(
        { name, productArea: PRODUCT_AREAS.ANALYTICS, subProductArea: SUB_PRODUCT_AREAS.SEGMENTS },
        eligibleVisitorPipeline({ segment, livePreview: !!livePreview, noLimit, timeRange }),
    );
}

export default {
    name: 'segment-eligible-visitor-list',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.SEGMENTS,
    responseFormat: 'rows',
    build: eligibleVisitorPipeline,
};
